/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import proptypes from 'prop-types';
import { extend } from '@thd-nucleus/data-sources';
import { useStore } from '@thd-nucleus/experience-context';
import { ImpressionProvider } from '@thd-olt-component-react/impression';
import { dynamicRecsDataModel, dataModel as productLookupDataModel } from '../dataModel';
import { ProductPodV7Carousel } from '../core/ProductPodV7Carousel';
import {
  getDrecsComponent,
  getDrecsImpressionID,
  getDrecsimpressionType,
  getWrapperClass,
  isRange
} from '../utils/helpers';
import {
  topSellingRangesAnchorId,
  topSellingRangesItemIds,
  topSellingRangesSchemaName,
  topSellingRangesTitle
} from './topSellingRangesList';
import '../../styles/thd-recs-containers.scss';

const {
  bool: boolType, string: stringType, shape
} = proptypes;

export const isTopSellingRangesEligible = ({ enableTopSellingRanges, recsContext, schemaName }) => Boolean(
  enableTopSellingRanges && isRange(recsContext) && schemaName === topSellingRangesSchemaName
);

const TopSellingRanges = ({
  anchorId = topSellingRangesAnchorId,
  schemaName = topSellingRangesSchemaName,
  anchorImage = null,
  showLoading = true,
  hideSwatches,
  hideATC = false,
  hideFavorites = true,
  parent,
  zoneName = '',
  zonePosition = '',
  impressionData = { id: null, component: null, type: null },
  name = '',
  noPipSpacing = false,
  ...rest
}) => {
  const {
    storeId, storeZip: zipCode, isLocalized, membershipInformation
  } = useStore();

  // useStore reports isLocalized as soon as clientStore exists, even while it still holds the
  // default store and no zip. Pods query catalog data themselves, so keep them unmounted until
  // the real store values land or every pod fetches twice.
  const isStoreReady = Boolean(isLocalized && storeId && zipCode);

  const products = isStoreReady
    ? topSellingRangesItemIds.map((itemId) => ({ product: { itemId } }))
    : [];

  const carouselData = {
    metadata: { apiName: schemaName, title: topSellingRangesTitle },
    categories: [],
    products
  };

  // Carousel data is derived here, so callers cannot override it through the passthrough props.
  const definedProps = Object.fromEntries(
    Object.entries(rest).filter(([key, value]) => value !== undefined && !['data', 'loading', 'podResults'].includes(key))
  );

  const carouselProps = {
    anchorId,
    anchorImage,
    itemClass: 'fixed-item',
    scheme: schemaName,
    data: carouselData,
    podResults: products.length,
    loading: !isStoreReady,
    storeId,
    zipCode,
    membershipInformation,
    hideSwatches,
    hideATC,
    hideFavorites,
    // Ranges are major appliances, so keep fulfillment in the add-to-cart button.
    noATCFulfillment: false,
    parent: parent || `recs-product-pod-carousel-${schemaName}`,
    analyticsAnchorProductSku: anchorId?.split(',')[0],
    zoneName,
    zonePosition,
    telemetryKey: topSellingRangesTitle,
    ...definedProps
  };

  const wrapperClasses = getWrapperClass(schemaName, showLoading, carouselProps.loading, false, noPipSpacing);

  return (
    <ImpressionProvider
      data={{
        id: getDrecsImpressionID(impressionData),
        component: getDrecsComponent(impressionData),
        name: topSellingRangesTitle,
        type: getDrecsimpressionType(impressionData),
        recType: schemaName || '',
        position: '',
        category: '',
        categoryPosition: '',
        ...(name ? { sectionSlotID: name } : {}),
        ...(impressionData?.recType ? { recType: impressionData.recType } : {})
      }}
    >
      <div
        id={schemaName}
        className={wrapperClasses}
        data-type="container"
        data-component={`DynamicRecs-${schemaName}`}
      >
        <meta data-prop="name" content={schemaName} />
        <ProductPodV7Carousel
          {...carouselProps}
          tntTitle={topSellingRangesTitle}
          enableTopSellingRanges
        />
      </div>
    </ImpressionProvider>
  );
};

TopSellingRanges.displayName = 'TopSellingRanges';

TopSellingRanges.propTypes = {
  anchorId: stringType,
  schemaName: stringType,
  anchorImage: stringType,
  showLoading: boolType,
  hideSwatches: boolType,
  hideATC: boolType,
  hideFavorites: boolType,
  parent: stringType,
  zoneName: stringType,
  zonePosition: stringType,
  impressionData: shape({
    id: stringType,
    component: stringType,
    type: stringType
  }),
  name: stringType,
  noPipSpacing: boolType
};

TopSellingRanges.dataModel = extend(productLookupDataModel, dynamicRecsDataModel);

export default TopSellingRanges;
